import React, { Component, ReactElement } from 'react';
import * as KeyCode from '~/utils/KeyCode';
import LayoutConstraint from '~/UI/LayoutConstraint/LayoutConstraint';

import SegmentedSelectPage, {
	type FlattenedOptionsResult,
	flattenOptions,
	type SegmentedSelectReactElement,
} from './Page/SegmentedSelectPage';
import SegmentedSelectPopover from './Popover/SegmentedSelectPopover';

export interface SelectedOption {
	value: any;
	parentValue: any;
	prompt: React.ReactNode;
}

interface Props {
	dataTestId?: string;
	selectedOption: SelectedOption | null;
	children: React.ReactNode;
	prompt: React.ReactNode;
	filterInput?: React.ReactNode;
	isValid: boolean;
	disabled: boolean;
	styles: {
		popover: string;
		prompt: string;
		header?: string | null;
		promptContent: string;
		invalidPrompt?: string;
		disabledPrompt: string;
		promptFilter: string;
	};
	popoverWidth: number | null;
	onSelect(option: SelectedOption | null): void;
}

interface State {
	expanded: boolean;
	currentPageIndex: number;
}

export default class SegmentedSelect extends Component<Props, State> {
	static defaultProps = {
		prompt: null,
		isValid: true,
		selectedOption: null,
		popoverWidth: null,
		disabled: false,
	};

	state: State = {
		expanded: false,
		currentPageIndex: 0,
	};

	promptRef: { current: HTMLDivElement | null } = React.createRef();

	currentPageRef: SegmentedSelectPage | null = null;

	componentDidUpdate(prevProps: Props) {
		const { selectedOption } = this.props;

		if (selectedOption === null && this.state.currentPageIndex > 0) {
			this.setState({ currentPageIndex: 0 });
			return;
		}

		if (selectedOption !== prevProps.selectedOption && selectedOption !== null) {
			const segmentedPages = React.Children.toArray(this.props.children) as ReactElement<
				SegmentedSelectPage['props'],
				SegmentedSelectPage['render']
			>[];

			const segmentedPagesChildren = (segmentedPages[0]?.props.children || []) as SegmentedSelectReactElement[];
			const options = flattenOptions(segmentedPagesChildren);
			const option = options.find((option) => option.option.props.value === selectedOption.value);
			if (option !== undefined && option.option.props.hasSubOptions) {
				this.setState({ expanded: true, currentPageIndex: 1 });
			}
		}
	}

	renderPrompt() {
		const {
			props: { isValid, filterInput, disabled, styles },
			state: { expanded },
		} = this;
		const hasFilter = expanded && filterInput;

		return (
			<div
				className={
					hasFilter
						? styles.promptFilter
						: disabled
						? styles.disabledPrompt
						: isValid
						? styles.prompt
						: styles.invalidPrompt
				}
				data-testid={this.props.dataTestId || 'segmented-select-prompt'}
				role='button'
				tabIndex={0}
				onKeyDown={disabled ? undefined : this.handleKeyDown}
				onMouseUp={disabled ? undefined : this.promptClicked}
			>
				<div className={styles.promptContent} ref={this.promptRef}>
					{hasFilter ? filterInput : this.renderPromptContent()}
				</div>
			</div>
		);
	}

	renderPromptContent() {
		const { selectedOption, prompt } = this.props;
		return selectedOption?.prompt ?? prompt ?? null;
	}

	promptClicked = () => {
		const { expanded } = this.state;
		if (!expanded) {
			this.setState({ expanded: !expanded });
		}
	};

	closed = (event: MouseEvent) => {
		const promptElement = this.promptRef.current;
		// @ts-expect-error Argument of type 'EventTarget | null' is not assignable to parameter of type 'Node | null'.
		if (!promptElement || (event.target !== promptElement && !promptElement.contains(event.target))) {
			this.collapse();
		}
	};

	handleSelect = (option: FlattenedOptionsResult) => {
		const { selectedOption, onSelect } = this.props;

		if (option === null) {
			this.collapse();
			onSelect(null);
			return;
		}

		const { value, children, prompt, hasSubOptions } = option.option.props;

		onSelect({
			value,
			parentValue: hasSubOptions ? value : selectedOption ? selectedOption.parentValue : null,
			prompt: hasSubOptions ? null : prompt !== undefined ? prompt : children,
		});

		if (hasSubOptions) {
			this.setState((prevState) => ({
				currentPageIndex: prevState.currentPageIndex + 1,
			}));
		} else {
			this.collapse();
		}
	};

	handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.keyCode === KeyCode.ESCAPE) {
			this.collapse();
			return;
		}

		if (this.currentPageRef !== null) {
			this.currentPageRef.handleKeyDown(event);
			return;
		}

		switch (event.keyCode) {
			case KeyCode.UP:
			case KeyCode.DOWN:
			case KeyCode.ENTER:
			case KeyCode.PAGE_UP:
			case KeyCode.PAGE_DOWN:
				event.preventDefault();

				this.setState({ expanded: true }, () => {
					if (this.currentPageRef !== null) {
						this.currentPageRef.handleKeyDown(event);
					}
				});
				break;

			default:
			// intentionally blank
		}
	};

	collapse() {
		this.setState({ expanded: false });
	}

	render() {
		const {
			props: { selectedOption, children, popoverWidth, styles },
			state: { expanded, currentPageIndex },
		} = this;

		return (
			<LayoutConstraint reference={this.renderPrompt()}>
				{expanded ? (
					<SegmentedSelectPopover
						createPageProps={(page, pageIndex) => ({
							selectedOption,
							styles: {
								...styles,
								header: styles.header || null,
							},
							ref: pageIndex === currentPageIndex ? (ref: SegmentedSelectPage) => (this.currentPageRef = ref) : null,
							onSelect: this.handleSelect,
						})}
						currentPageIndex={currentPageIndex}
						selectedOption={selectedOption}
						styles={styles}
						width={popoverWidth}
						onClose={this.closed}
					>
						{children}
					</SegmentedSelectPopover>
				) : null}
			</LayoutConstraint>
		);
	}
}
